/**
 * WalletLayout — Phase-2 multi-currency wallet page.
 *
 * Renders a Stake-style 4-row balance list (BDT / INR / PKR / USDT). Each row
 * shows the currency's REAL balance with two buttons (Deposit / Withdraw).
 * Below is a compact Demo section with per-currency Refill buttons.
 *
 * The old subroute nav (Deposit / Withdraw / KYC as tabs) is kept as a small
 * chip strip at the top so users can still navigate to the KYC page. Deposit
 * and Withdraw rows drive those subroutes with a `?currency=CODE` query.
 */
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { ArrowDownToLine, ArrowUpFromLine, ShieldCheck, RefreshCw } from "lucide-react";
import { Toaster, toast } from "sonner";
import { useCallback, useEffect, useState } from "react";

import AppShell from "../components/ui-r1/AppShell";
import ChatWidget from "../components/ChatWidget";
import Button from "../components/ui-r1/Button";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useCurrency } from "../context/CurrencyContext";
import {
  CURRENCIES,
  DEPOSIT_ENABLED,
  WITHDRAW_ENABLED,
  formatAmount,
  getBalance,
} from "../lib/currency";
import {
  formatWagerLine,
  isActiveStatus,
  progressPct,
} from "../lib/bonusHelpers";

const NAV = [
  { to: "/wallet/deposit", label: "Deposit", icon: ArrowDownToLine, testid: "wallet-tab-deposit" },
  { to: "/wallet/withdraw", label: "Withdraw", icon: ArrowUpFromLine, testid: "wallet-tab-withdraw" },
  { to: "/wallet/kyc", label: "KYC", icon: ShieldCheck, testid: "wallet-tab-kyc" },
];

export default function WalletLayout() {
  const { user, refresh } = useAuth();
  const loc = useLocation();
  const [claims, setClaims] = useState([]);

  // Pull the user's active bonus claims once — used both for the top-of-
  // page wagering meter and for the per-row deposit/withdraw guards below.
  const reloadClaims = useCallback(async () => {
    try {
      const { data } = await api.get("/bonuses/mine");
      setClaims(data?.items || []);
    } catch {
      setClaims([]);
    }
  }, []);
  useEffect(() => { if (user) reloadClaims(); }, [user, reloadClaims]);
  const activeClaims = (claims || []).filter((c) => isActiveStatus(c.status));
  const activeCurrencies = new Set(activeClaims.map((c) => c.currency));

  // Fetch fresh balances on mount + whenever a subroute navigation lands here
  // (deposit page just credited BDT → we want to see the update).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { refresh(); }, [loc.pathname]);

  if (!user) return null;

  // Only show the top summary block on the wallet root — subroutes have their
  // own headers (Deposit / Withdraw / KYC) which would otherwise stack.
  const isRoot = loc.pathname === "/wallet" || loc.pathname === "/wallet/";

  return (
    <AppShell activeSection="wallet" pageTitle="Wallet">
      <Toaster theme="dark" position="top-center" />
      <div className="max-w-5xl mx-auto px-4 sm:px-8 py-6" data-testid="wallet-layout">
        <div className="mb-6">
          <div className="text-[10px] tracking-[0.4em] uppercase text-pink">Wallet</div>
          <h1 className="font-display italic text-3xl sm:text-4xl text-ink">
            Your cash flow, at a glance.
          </h1>
        </div>

        {isRoot && (
          <>
            {activeClaims.length > 0 && (
              <WageringMeter claims={activeClaims} onReload={reloadClaims} />
            )}
            <MultiCurrencyBalances user={user} onRefill={refresh} activeCurrencies={activeCurrencies} />
          </>
        )}

        {/* Subroute chip nav — still useful to reach KYC directly */}
        <nav className="flex gap-1 mb-6 p-1 rounded-lg bg-elevated border border-line" data-testid="wallet-tabs">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              className={({ isActive }) =>
                "flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-md text-[11px] tracking-[0.2em] uppercase transition " +
                (isActive
                  ? "bg-brand-gradient text-white shadow-glow-pink"
                  : "text-ink-2 hover:text-ink hover:bg-highest")
              }
              data-testid={n.testid}
            >
              <n.icon className="w-3.5 h-3.5" />
              {n.label}
            </NavLink>
          ))}
        </nav>

        <Outlet />
      </div>
      <ChatWidget />
    </AppShell>
  );
}

function MultiCurrencyBalances({ user, onRefill, activeCurrencies }) {
  const nav = useNavigate();
  const { setActiveCurrency } = useCurrency();
  const [busyRefill, setBusyRefill] = useState(null);

  const goDeposit = useCallback((code) => {
    setActiveCurrency(code);
    nav(`/wallet/deposit?currency=${code}`);
  }, [nav, setActiveCurrency]);

  const goWithdraw = useCallback((code) => {
    setActiveCurrency(code);
    nav(`/wallet/withdraw?currency=${code}`);
  }, [nav, setActiveCurrency]);

  const refill = useCallback(async (code) => {
    setBusyRefill(code);
    try {
      await api.post("/wallet/demo-topup", { currency: code });
      toast.success(`Demo ${code} refilled`);
      await onRefill();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Refill failed");
    } finally {
      setBusyRefill(null);
    }
  }, [onRefill]);

  return (
    <div className="mb-6 space-y-6">
      {/* Real balances */}
      <section data-testid="wallet-balances-real">
        <div className="flex items-center justify-between mb-2">
          <div className="text-[10px] tracking-[0.35em] uppercase text-ink-2">Real balance</div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-ink-3">4 currencies</div>
        </div>
        <div className="rounded-xl border border-line bg-elevated overflow-hidden">
          {CURRENCIES.map((c, i) => {
            const bal = getBalance(user, "real", c.code);
            const depEnabled = DEPOSIT_ENABLED[c.code];
            const wdEnabled = WITHDRAW_ENABLED[c.code];
            return (
              <div
                key={c.code}
                className={
                  "flex items-center gap-4 px-4 py-4 " +
                  (i > 0 ? "border-t border-line" : "")
                }
                data-testid={`wallet-real-row-${c.code}`}
              >
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  <span className="w-9 h-9 rounded-lg bg-highest border border-line flex items-center justify-center text-pink font-mono">
                    {c.symbol}
                  </span>
                  <div className="min-w-0">
                    <div className="text-[10px] tracking-widest uppercase text-ink-3">{c.code}</div>
                    <div className="font-display italic text-xl gold-text truncate" data-testid={`wallet-real-balance-${c.code}`}>
                      {formatAmount(bal, c.code)}
                    </div>
                  </div>
                </div>
                <div className="flex gap-2 shrink-0">
                  <button
                    type="button"
                    onClick={() => depEnabled && goDeposit(c.code)}
                    disabled={!depEnabled}
                    title={depEnabled ? "" : "Deposits coming soon"}
                    className={
                      "text-[10px] tracking-widest uppercase px-3 py-2 rounded-md border " +
                      (depEnabled
                        ? "border-pink/40 text-pink bg-pink/5 hover:bg-pink/15"
                        : "border-line text-ink-3 cursor-not-allowed")
                    }
                    data-testid={`wallet-real-deposit-${c.code}`}
                  >
                    Deposit
                  </button>
                  {(() => {
                    const wagerLocked = (activeCurrencies || new Set()).has(c.code);
                    const canWithdraw = wdEnabled && !wagerLocked;
                    // Precedence: wager-lock message wins over "not enabled",
                    // because for BDT/INR/PKR (WITHDRAW_ENABLED=false) the
                    // player still needs to know why the button says "Locked".
                    const tooltip = wagerLocked
                      ? "Complete or abandon your wagering first"
                      : !wdEnabled
                      ? "Withdrawals coming soon"
                      : "";
                    return (
                      <button
                        type="button"
                        onClick={() => canWithdraw && goWithdraw(c.code)}
                        disabled={!canWithdraw}
                        title={tooltip}
                        className={
                          "text-[10px] tracking-widest uppercase px-3 py-2 rounded-md border " +
                          (canWithdraw
                            ? "border-line text-ink hover:bg-highest"
                            : "border-line text-ink-3 cursor-not-allowed")
                        }
                        data-testid={`wallet-real-withdraw-${c.code}`}
                      >
                        {wagerLocked ? "Locked" : "Withdraw"}
                      </button>
                    );
                  })()}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Demo balances */}
      <section data-testid="wallet-balances-demo">
        <div className="flex items-center justify-between mb-2">
          <div className="text-[10px] tracking-[0.35em] uppercase text-ink-2">Demo balance</div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-ink-3">Practice · non-cash</div>
        </div>
        <div className="rounded-xl border border-line bg-elevated overflow-hidden">
          {CURRENCIES.map((c, i) => {
            const bal = getBalance(user, "demo", c.code);
            return (
              <div
                key={c.code}
                className={
                  "flex items-center gap-4 px-4 py-3 " +
                  (i > 0 ? "border-t border-line" : "")
                }
                data-testid={`wallet-demo-row-${c.code}`}
              >
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  <span className="w-8 h-8 rounded-lg bg-base border border-line flex items-center justify-center text-ink-2 font-mono text-xs">
                    {c.symbol}
                  </span>
                  <div className="min-w-0">
                    <div className="text-[10px] tracking-widest uppercase text-ink-3">{c.code}</div>
                    <div className="text-sm text-ink-2 truncate" data-testid={`wallet-demo-balance-${c.code}`}>
                      {formatAmount(bal, c.code)}
                    </div>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => refill(c.code)}
                  loading={busyRefill === c.code}
                  data-testid={`wallet-demo-refill-${c.code}`}
                >
                  <RefreshCw className="w-3.5 h-3.5 mr-1.5" />
                  Refill
                </Button>
              </div>
            );
          })}
        </div>
      </section>
      {/* Bonus balances — parallel to Real / Demo. Always show all 4 rows
          (even at 0.00) for parity with the Real/Demo sections and to
          make clear that bonus balance is a supported feature per
          currency, not something that pops in unexpectedly. */}
      <section data-testid="wallet-balances-bonus">
        <div className="flex items-center justify-between mb-2">
          <div className="text-[10px] tracking-[0.35em] uppercase text-gold">Bonus balance</div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-ink-3">Wagered → converts to real</div>
        </div>
        <div className="rounded-xl border border-gold/40 bg-elevated overflow-hidden">
          {CURRENCIES.map((c, i) => {
            const bal = getBalance(user, "bonus", c.code);
            const isFunded = bal > 0;
            return (
              <div
                key={c.code}
                className={"flex items-center gap-4 px-4 py-3 " + (i > 0 ? "border-t border-line" : "")}
                data-testid={`wallet-bonus-row-${c.code}`}
              >
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  <span
                    className={
                      "w-8 h-8 rounded-lg border flex items-center justify-center font-mono text-xs " +
                      (isFunded
                        ? "bg-gold/10 border-gold/40 text-gold"
                        : "bg-base border-line text-ink-3")
                    }
                  >
                    {c.symbol}
                  </span>
                  <div className="min-w-0">
                    <div className="text-[10px] tracking-widest uppercase text-ink-3">{c.code}</div>
                    <div
                      className={"text-sm truncate " + (isFunded ? "text-gold" : "text-ink-3")}
                      data-testid={`wallet-bonus-balance-${c.code}`}
                    >
                      {formatAmount(bal, c.code)}
                    </div>
                  </div>
                </div>
                <div className="text-[10px] tracking-widest uppercase text-ink-3">
                  {isFunded ? "Locked to wagering" : "No bonus"}
                </div>
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
}

// Compact top-of-wallet wagering summary. One card per active claim; each
// shows required/completed + progress bar + max-bet warning + abandon link.
function WageringMeter({ claims, onReload }) {
  const [busy, setBusy] = useState(null);
  const abandon = async (c) => {
    if (!window.confirm(`Abandon wagering in ${c.currency}? Bonus balance will be zeroed. Real is untouched.`)) return;
    setBusy(c.claim_id);
    try {
      await api.post(`/bonuses/claims/${c.claim_id}/abandon`);
      toast.success("Wagering abandoned");
      await onReload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Abandon failed");
    } finally {
      setBusy(null);
    }
  };
  return (
    <section className="mb-6" data-testid="wallet-wagering-meter">
      <div className="text-[10px] tracking-[0.35em] uppercase text-gold mb-2">Active wagering</div>
      <div className="space-y-2">
        {claims.map((c) => {
          const pct = progressPct(c);
          return (
            <div
              key={c.claim_id}
              className="rounded-xl border border-gold/40 bg-elevated p-4"
              data-testid={`wallet-wager-${c.claim_id}`}
            >
              <div className="flex items-start justify-between gap-3 mb-2">
                <div className="min-w-0">
                  <div className="font-display italic text-lg text-ink truncate">{c.bonus_name}</div>
                  <div className="text-[11px] text-ink-3">
                    {c.currency} · max bet {formatAmount(c.max_bet || 0, c.currency)}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="font-mono text-sm text-gold">{formatWagerLine(c)}</div>
                  <div className="text-[10px] tracking-widest uppercase text-ink-3">{pct}%</div>
                </div>
              </div>
              <div className="h-1.5 bg-highest rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-pink to-gold transition-[width] duration-500"
                  style={{ width: `${pct}%` }}
                />
              </div>
              <div className="flex items-center justify-between mt-2 text-[10px] tracking-widest uppercase">
                <span className="text-ink-3">
                  Wins during wager stay in bonus until 100%.
                </span>
                <button
                  onClick={() => abandon(c)}
                  disabled={busy === c.claim_id}
                  className="text-danger hover:underline disabled:opacity-50"
                  data-testid={`wallet-wager-abandon-${c.claim_id}`}
                >
                  {busy === c.claim_id ? "Working…" : "Abandon bonus"}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
